import { hash } from 'rsvp';
import type { DS } from 'ember-data';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import ProtectedRoute from 'uplisting-frontend/pods/base/protected-route';
import { markInboxMessageRead } from 'uplisting-frontend/pods/-sidebar/utils';
import SidebarBookingRequestsMessagesController from 'uplisting-frontend/pods/-sidebar/booking-requests/messages/controller';

interface IParams {
  replyId?: string;
}

interface IModel {
  bookingRequest: ModelRegistry['booking-request'];
  reply?: Promise<ModelRegistry['contextual-reply']>;
  messagingChannels: Promise<
    DS.PromiseArray<ModelRegistry['messaging-channel']>
  >;
}

export default abstract class SidebarBookingRequestsMessagesRoute extends ProtectedRoute {
  @service('repositories/contextual-reply')
  contextualReplyRepository!: Services['repositories/contextual-reply'];

  @service('repositories/messaging-channel')
  messagingChannelRepository!: Services['repositories/messaging-channel'];

  @service sentry!: Services['sentry'];

  abstract parentRoute: string;

  model(params: IParams) {
    const bookingRequest = this.modelFor(
      this.parentRoute,
    ) as ModelRegistry['booking-request'];

    const data: IModel = {
      bookingRequest,
      messagingChannels: this.messagingChannelRepository.query({
        booking_request_id: bookingRequest.id,
      }),
    };

    if (params.replyId) {
      data.reply = this.contextualReplyRepository.find(
        params.replyId,
        data.bookingRequest,
      );
    }

    return hash(data);
  }

  setupController(
    controller: SidebarBookingRequestsMessagesController,
    model,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    Object.assign(controller, model);
  }

  afterModel(model: IModel, transition: Transition): void {
    super.afterModel(model, transition);

    void markInboxMessageRead(this, model.bookingRequest.inboxMessage);
  }

  resetController(
    controller: SidebarBookingRequestsMessagesController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    controller.reply = undefined;
    controller.replyId = undefined;

    this.contextualReplyRepository.unloadAll();
  }
}
